import { Brandmark, Button, SlashLabel } from "@/components/primitives";
import { useClerk, useSignIn } from "@clerk/react";
import { isClerkAPIResponseError } from "@clerk/react/errors";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Per spec, OAuth failure (user not registered + sign-ups restricted) should
// land at "/" with ?signup_restricted=1. IndexRedirect propagates the param
// onward to /sign-in?signup_restricted=1 where the banner is rendered.
const SIGNUP_RESTRICTED_URL = "/?signup_restricted=1";

export function SignInSsoCallback() {
  const { handleRedirectCallback } = useClerk();
  const { signIn } = useSignIn();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Intercept the navigation decided by handleRedirectCallback. The rule
    // is simple: signIn.status === "complete" means a session was created,
    // so honor whatever URL Clerk wants. Anything else (transferable to
    // sign-up, needs_identifier, hosted Account Portal redirect, …) is a
    // sign-in failure that should route to /?signup_restricted=1.
    const customNavigate = async (to: string): Promise<unknown> => {
      if (signIn.status !== "complete") {
        navigate(SIGNUP_RESTRICTED_URL, { replace: true });
        return;
      }

      let url: URL | null = null;
      try {
        url = new URL(to, window.location.origin);
      } catch {
        navigate(to, { replace: true });
        return;
      }

      if (url.origin !== window.location.origin) {
        // Even on success, don't leave our app for a Clerk-hosted page.
        navigate("/", { replace: true });
        return;
      }
      navigate(url.pathname + url.search + url.hash, { replace: true });
    };

    handleRedirectCallback(
      {
        signInUrl: SIGNUP_RESTRICTED_URL,
        signUpUrl: SIGNUP_RESTRICTED_URL,
        signInFallbackRedirectUrl: SIGNUP_RESTRICTED_URL,
        signUpFallbackRedirectUrl: SIGNUP_RESTRICTED_URL,
        signUpForceRedirectUrl: SIGNUP_RESTRICTED_URL,
        continueSignUpUrl: SIGNUP_RESTRICTED_URL,
      },
      customNavigate,
    ).catch((err: unknown) => {
      if (isClerkAPIResponseError(err)) {
        setError(
          err.errors[0]?.longMessage ||
            err.errors[0]?.message ||
            "SSO sign-in failed.",
        );
      } else {
        setError("SSO sign-in failed. Please try again.");
      }
    });
  }, [handleRedirectCallback, navigate, signIn]);

  return (
    <div className="signin-callback">
      <div className="signin-callback-inner">
        <Brandmark />
        {error ? (
          <>
            <SlashLabel>SSO sign-in failed</SlashLabel>
            <div
              style={{
                fontSize: 13,
                color: "var(--ink-2)",
                maxWidth: 360,
                textAlign: "center",
              }}
            >
              {error}
            </div>
            <Button onClick={() => navigate("/sign-in", { replace: true })}>
              Back to sign-in
            </Button>
          </>
        ) : (
          <>
            <SlashLabel>Completing SSO sign-in…</SlashLabel>
            <div
              className="shimmer"
              style={{ width: 200, height: 8, borderRadius: 4 }}
            />
          </>
        )}
      </div>
    </div>
  );
}
